import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CContainer, CCard, CCardHeader, CCardBody, CSpinner,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, COffcanvas, COffcanvasBody
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilMediaPlay, cilSettings, cilMap, cilFolderOpen } from "@coreui/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";

import CameraHeader from "./components/CameraHeader";
import CameraFilterPanel from "./components/CameraFilterPanel";
import CameraVideoPlayer from "./components/CameraVideoPlayer";
import CameraArchiveList from "./components/CameraArchiveList";
import CameraTabs from "./components/CameraTabs";
import CameraModals from "./components/CameraModals";
import RoadMarkingDashboard from "./components/RoadMarkingDashboard";
import GarbageTimelineSection from "./components/GarbageTimelineSection";
import TruckMap from "./components/TruckMap";

import { fromFakeLocalToUtcIso, getFakeLocalDate, getDeviceOffsetMin } from "./utils/timeUtils";
import { createStore } from "./utils/truckActivity";
import "./CameraDetailPage.css";

registerLocale("ru", ru);

const VIDEO_FPS = 24.0;

const RAZM_CLASS_COLORS = {
  u00001: "#00e1ff",
  u00002: "#58ff4d",
  u00003: "#ff0000",
  u00004: "#ffd500",
  u00005: "#ff9500",
};

const RAZM_CLASS_LABELS = {
  u00001: "Отверстие",
  u00002: "Мешок",
  u00003: "Погрузка-мешок",
  u00004: "Ведро",
  u00005: "Погрузка-ведро",
};

const videoSecToDbMs = (sec, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0) return fallbackStartMs + sec * 1000;
  const targetF = sec * VIDEO_FPS;
  let l = 0, r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    if (mapping[m].f === targetF) return new Date(mapping[m].t).getTime();
    if (mapping[m].f < targetF) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length) return new Date(mapping[mapping.length - 1].t).getTime();
  if (r < 0) return new Date(mapping[0].t).getTime();
  return new Date(mapping[l].t).getTime();
};

const RoadMarkingDetailPage = () => {
  const { t } = useTranslation();
  const { cameraId } = useParams();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isStaff = localStorage.getItem("is_staff") === "true" || isSuperuser;

  const [device, setDevice] = useState(null);
  const [folders, setFolders] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolderStartTime, setCurrentFolderStartTime] = useState(null);
  const [currentAbsoluteTime, setCurrentAbsoluteTime] = useState(null);
  const [clickedTimeMs, setClickedTimeMs] = useState(null);
  const [archiveRightPanelMode, setArchiveRightPanelMode] = useState("map");

  const [analyticsData, setAnalyticsData] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [datePreset, setDatePreset] = useState("today");
  const [datesInitialized, setDatesInitialized] = useState(false);

  // Панель настроек (Offcanvas)
  const [offcanvasVisible, setOffcanvasVisible] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState("settings");
  const [schedule, setSchedule] = useState({
    shift_start: "00:00",
    shift_end: "23:59",
    work_days: 127,
    timezone_offset: 0,
    max_cycle_time_sec: 180,
    downtime_threshold_sec: 300,
    has_multiple_shifts: false,
    realtime_stream_enabled: false,
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ name: "Дневная", start_time: "08:00", end_time: "20:00" });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({ start_time: "12:00", end_time: "13:00", description: "Обед" });
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({
    startDate: new Date(), endDate: new Date(), is_working: false, is_maintenance: false, description: ""
  });
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    name: "Термопластик", is_default: true, startDate: new Date(), endDate: new Date(),
    bulk_density: 1500, true_density: 2000, fill_factor: 1.0, loosening_factor: 1.0
  });

  // Стейты форматирования и восстановления
  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [formatMode, setFormatMode] = useState("all");
  const [formatDate, setFormatDate] = useState(null);
  const [formatConfirmation, setFormatConfirmation] = useState("");
  const [formatting, setFormatting] = useState(false);
  const [formatActionTab, setFormatActionTab] = useState("hide");
  const [formatStats, setFormatStats] = useState(null);

  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [recalcModalVisible, setRecalcModalVisible] = useState(false);
  const [recalcStartDate, setRecalcStartDate] = useState(null);
  const [recalculating, setRecalculating] = useState(false);

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);
  const customPlayerRef = useRef(null);
  const canvasRef = useRef(null);
  const mapViewRef = useRef({});
  const playheadStore = useMemo(() => createStore(null), []);

  const [detections, setDetections] = useState({});
  const [showDetections, setShowDetections] = useState(true);
  const [framesMapping, setFramesMapping] = useState([]);
  const framesMappingRef = useRef([]);

  useEffect(() => {
    framesMappingRef.current = framesMapping;
  }, [framesMapping]);

  const openFormatModal = async () => {
    setFormatActionTab("hide");
    setFormatConfirmation("");
    setFormatModalVisible(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/clear-data/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormatStats(res.data);
    } catch (e) {
      console.warn("Не удалось загрузить статус скрытых папок", e);
    }
  };

  const handleClearData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "hide", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, device?.timezone_offset || 0);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Данные успешно скрыты");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при скрытии данных");
    } finally {
      setFormatting(false);
    }
  };

  const handleRestoreData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "restore", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, device?.timezone_offset || 0);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Видимость успешно восстановлена");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при восстановлении данных");
    } finally {
      setFormatting(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const loadInit = async () => {
      try {
        const [resDev, resFolders, resDates, resShifts, resBreaks] = await Promise.all([
          axios.get(`/api/devices/${cameraId}/`, { headers }),
          axios.get(`/api/devices/${cameraId}/folders/`, { headers }),
          axios.get(`/api/devices/${cameraId}/available-dates/`, { headers }).catch(() => ({ data: [] })),
          axios.get(`/api/devices/${cameraId}/shifts/`, { headers }).catch(() => ({ data: [] })),
          axios.get(`/api/devices/${cameraId}/breaks/`, { headers }).catch(() => ({ data: [] })),
        ]);

        const devData = resDev.data;
        setDevice(devData);
        setFolders(resFolders.data || []);
        setShifts(Array.isArray(resShifts.data) ? resShifts.data : resShifts.data.results || []);
        setBreaks(Array.isArray(resBreaks.data) ? resBreaks.data : resBreaks.data.results || []);

        if (resDates.data) {
          setAvailableDates(
            resDates.data.map((dStr) => {
              const [y, m, d] = dStr.split("-").map(Number);
              return new Date(y, m - 1, d);
            })
          );
        }

        setSchedule({
          shift_start: devData.shift_start ? devData.shift_start.substring(0, 5) : "00:00",
          shift_end: devData.shift_end ? devData.shift_end.substring(0, 5) : "23:59",
          work_days: devData.work_days || 127,
          timezone_offset: devData.timezone_offset || 0,
          max_cycle_time_sec: devData.max_cycle_time_sec || 180,
          downtime_threshold_sec: devData.downtime_threshold_sec || 300,
          has_multiple_shifts: devData.has_multiple_shifts || false,
          realtime_stream_enabled: devData.realtime_stream_enabled || false,
        });

        const offsetMin = getDeviceOffsetMin(devData.timezone_offset || 0);
        const now = dayjs().utcOffset(offsetMin);
        const s = now.startOf("day").toDate();
        const e = now.endOf("day").toDate();
        setStartDate(s);
        setEndDate(e);
        setRecalcStartDate(s);
        setFormatDate(e);
        setDatesInitialized(true);

        fetchRoadMarkingAnalytics(s, e, devData.timezone_offset || 0);
      } catch (err) {
        toast.error("Ошибка загрузки данных устройства");
      }
    };
    loadInit();
  }, [cameraId]);

  const fetchRoadMarkingAnalytics = async (s = startDate, e = endDate, tz = device?.timezone_offset || 0) => {
    if (!s || !e) return;
    setLoadingAnalytics(true);
    setTrajectoryData([]);
    try {
      const token = localStorage.getItem("token");
      const utcStart = fromFakeLocalToUtcIso(s, tz);
      const utcEnd = fromFakeLocalToUtcIso(e, tz);

      const res = await axios.get("/api/road-marking-analytics/", {
        headers: { Authorization: `Bearer ${token}` },
        params: { device_id: cameraId, start_date: utcStart, end_date: utcEnd },
      });
      setAnalyticsData(res.data);

      if (res.data?.loading_dates) {
        setLoadingDates(
          res.data.loading_dates.map((dStr) => {
            const [y, m, d] = dStr.split("-").map(Number);
            return new Date(y, m - 1, d);
          })
        );
      }

      try {
        const trajRes = await axios.get(`/api/devices/${cameraId}/trajectory/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: utcStart, end_date: utcEnd, step_sec: 5, with_speed: 1 },
        });
        setTrajectoryData(trajRes.data.map((pt) => ({ ...pt, ts: getFakeLocalDate(pt.ts, tz) })));
      } catch (_) {}
    } catch (err) {
      toast.error("Ошибка получения аналитики дорожной разметки");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;

    const tz = device?.timezone_offset || 0;
    const offsetMin = getDeviceOffsetMin(tz);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    let newStart = new Date(nowFake);
    let newEnd = new Date(nowFake);
    newEnd.setHours(23, 59, 59, 999);

    switch (preset) {
      case "today": newStart.setHours(0, 0, 0, 0); break;
      case "yesterday":
        newStart.setDate(newStart.getDate() - 1);
        newStart.setHours(0, 0, 0, 0);
        newEnd = new Date(newStart);
        newEnd.setHours(23, 59, 59, 999);
        break;
      case "last_2_days": newStart.setDate(newStart.getDate() - 1); newStart.setHours(0, 0, 0, 0); break;
      case "last_3_days": newStart.setDate(newStart.getDate() - 2); newStart.setHours(0, 0, 0, 0); break;
      case "last_7_days": newStart.setDate(newStart.getDate() - 6); newStart.setHours(0, 0, 0, 0); break;
      case "last_14_days": newStart.setDate(newStart.getDate() - 13); newStart.setHours(0, 0, 0, 0); break;
      case "last_month": newStart.setMonth(newStart.getMonth() - 1); newStart.setHours(0, 0, 0, 0); break;
      default: return;
    }
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchRoadMarkingAnalytics(newStart, newEnd, tz);
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/devices/${cameraId}/`, schedule, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Настройки успешно сохранены!");
    } catch (e) {
      toast.error("Ошибка сохранения настроек");
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleVideoSelect = (folder) => {
    if (!folder.video_file) return;
    setCurrentFolderStartTime(dayjs(folder.process_after).subtract(1, "hour"));
    setSelectedVideo(folder.video_file);
    setSelectedFolderId(folder.id);
    setSeekToTime(0);
    setSeekTriggerId(Date.now());
    setVideoModalVisible(true);
  };

  useEffect(() => {
    if (!selectedFolderId) {
      setDetections({});
      setFramesMapping([]);
      return;
    }
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    axios.get(`/api/devices/${cameraId}/folders/${selectedFolderId}/detections/`, { headers })
      .then((res) => setDetections(res.data))
      .catch(console.error);

    axios.get(`/api/folders/${selectedFolderId}/markup/`, { headers })
      .then((res) => setFramesMapping(res.data.frames_mapping || []))
      .catch(() => setFramesMapping([]));
  }, [selectedFolderId, cameraId]);

  useEffect(() => {
    let animId;
    let lastTimeUpdate = 0;

    const render = () => {
      const video = customPlayerRef.current?.getInternalPlayer();
      const canvas = canvasRef.current;

      if (video && video.readyState >= 2) {
        const now = Date.now();
        if (now - lastTimeUpdate > 250) {
          const fallbackMs = currentFolderStartTime ? currentFolderStartTime.valueOf() : Date.now();
          const currentDbMs = videoSecToDbMs(video.currentTime, framesMappingRef.current, fallbackMs);
          const fakeLocal = getFakeLocalDate(new Date(currentDbMs).toISOString(), device?.timezone_offset || 0);
          setCurrentAbsoluteTime(fakeLocal);
          playheadStore.set(fakeLocal.getTime());
          lastTimeUpdate = now;
        }

        if (canvas) {
          const ctx = canvas.getContext("2d");
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          if (canvas.width !== vw || canvas.height !== vh) {
            canvas.width = vw;
            canvas.height = vh;
          }
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (showDetections) {
            const fIdx = Math.round(video.currentTime * VIDEO_FPS);
            const objs = detections[fIdx] || detections[fIdx - 1] || detections[fIdx + 1];
            if (objs && objs.length > 0) {
              objs.forEach(([cls, x1, y1, x2, y2, conf]) => {
                if (!RAZM_CLASS_LABELS[cls]) return;
                const color = RAZM_CLASS_COLORS[cls] || "#ffffff";
                const sx = x1 * vw;
                const sy = y1 * vh;
                const sw = (x2 - x1) * vw;
                const sh = (y2 - y1) * vh;

                ctx.strokeStyle = color;
                ctx.lineWidth = 3;
                ctx.strokeRect(sx, sy, sw, sh);

                const label = `${RAZM_CLASS_LABELS[cls]} ${(conf * 100).toFixed(0)}%`;
                ctx.font = "bold 13px Arial";
                const textW = ctx.measureText(label).width + 10;
                ctx.fillStyle = color;
                ctx.fillRect(sx, Math.max(0, sy - 22), textW, 22);
                ctx.fillStyle = "#000000";
                ctx.fillText(label, sx + 5, Math.max(15, sy - 6));
              });
            }
          }
        }
      }
      animId = requestAnimationFrame(render);
    };
    render();
    return () => cancelAnimationFrame(animId);
  }, [detections, showDetections, currentFolderStartTime, device, playheadStore]);

  const handleTimeDoubleClick = async ({ exactTime }) => {
    if (!exactTime) return;
    const ms = new Date(exactTime).getTime();
    setClickedTimeMs(ms);
    playheadStore.set(ms);
    const fakeDate = new Date(exactTime);
    const utcIso = fromFakeLocalToUtcIso(fakeDate, device?.timezone_offset || 0);

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/video-frame/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { timestamp: utcIso },
      });
      const { video_url, seek_seconds, folder_id } = res.data;
      if (video_url !== selectedVideo) {
        setSelectedVideo(video_url);
        setSelectedFolderId(folder_id);
      }
      setSeekToTime(seek_seconds);
      setSeekTriggerId(Date.now());
      setCurrentAbsoluteTime(fakeDate);
      setVideoModalVisible(true);
      if (customPlayerRef.current) customPlayerRef.current.play();
    } catch (_) {
      toast.error("Не удалось найти видео для этого момента.");
    }
  };

  const handleRecalculateSubmit = async () => {
    setRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        "/api/analytics/recalculate/",
        {
          device_id: cameraId,
          start_date: fromFakeLocalToUtcIso(recalcStartDate, device?.timezone_offset || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || "Пересчет запущен");
      setRecalcModalVisible(false);
      setTimeout(() => fetchRoadMarkingAnalytics(), 2000);
    } catch (_) {
      toast.error("Ошибка при запуске пересчета");
    } finally {
      setRecalculating(false);
    }
  };

  const statusTimelineItems = useMemo(() => {
    if (!analyticsData?.timeline) return [];
    const tz = device?.timezone_offset || 0;
    return analyticsData.timeline.map((item, idx) => {
      const startMs = getFakeLocalDate(item.start, tz).getTime();
      const endMs = Math.max(startMs + 1000, getFakeLocalDate(item.end, tz).getTime());

      return {
        id: `status_${idx}`,
        start: startMs,
        end: endMs,
        color: item.color || "#3b82f6",
        label: item.label || "Движение",
        originalItem: item,
      };
    });
  }, [analyticsData, device]);

  const sessionsTimelineItems = useMemo(() => {
    if (!analyticsData?.sessions_summary) return [];
    const tz = device?.timezone_offset || 0;
    return analyticsData.sessions_summary.map((s, idx) => {
      const startMs = getFakeLocalDate(s.start, tz).getTime();
      const endMs = Math.max(startMs + 5000, getFakeLocalDate(s.end, tz).getTime());
      return {
        id: `sess_${s.id}`,
        start: startMs,
        end: endMs,
        color: s.bags_count >= s.buckets_count ? "#ff0000" : "#ff9500",
        label: `#${idx + 1}: Мешков ${s.bags_count} | Вёдер ${s.buckets_count}`,
        originalItem: s,
      };
    });
  }, [analyticsData, device]);

  const loadMapPoints = useMemo(() => {
    if (!analyticsData?.sessions_summary) return [];
    const tz = device?.timezone_offset || 0;
    return analyticsData.sessions_summary
      .filter((s) => s.latitude > 0 && s.longitude > 0)
      .map((s, idx) => {
        const fakeStart = getFakeLocalDate(s.start, tz);
        return {
          id: `razm_pt_${s.id}`,
          coords: [s.latitude, s.longitude],
          timeMs: fakeStart.getTime(),
          hint: `Загрузка #${idx + 1} (${dayjs(fakeStart).format("HH:mm")}) · Мешков: ${s.bags_count}, Вёдер: ${s.buckets_count}`,
        };
      });
  }, [analyticsData, device]);

  // Подготовка телеметрии для CameraTabs
  const telemetryDataForTabs = useMemo(() => {
    if (!analyticsData?.telemetry) return null;
    const telem = analyticsData.telemetry;
    const tz = device?.timezone_offset || 0;

    return {
      temp: [
        {
          id: "Температура АКБ (°C)",
          color: "hsl(30, 80%, 50%)",
          data: telem.chart_data.map((p) => ({
            x: getFakeLocalDate(p.ts, tz),
            y: p.cpu,
          })),
        },
        {
          id: "Реальный CPU (°C)",
          color: "hsl(0, 90%, 55%)",
          data: telem.chart_data.map((p) => ({
            x: getFakeLocalDate(p.ts, tz),
            y: p.cpu_real || p.cpu,
          })),
        },
      ],
      battery: [
        {
          id: "Заряд АКБ (%)",
          color: "hsl(120, 70%, 45%)",
          data: telem.chart_data.map((p) => ({
            x: getFakeLocalDate(p.ts, tz),
            y: p.batt,
          })),
        },
      ],
      voltage: [
        {
          id: "Напряжение АКБ (В)",
          color: "hsl(280, 75%, 60%)",
          data: telem.chart_data.map((p) => ({
            x: getFakeLocalDate(p.ts, tz),
            y: p.voltage || 0.0,
          })),
        },
      ],
      signal: [
        {
          id: "Signal (0-4)",
          color: "hsl(210, 80%, 55%)",
          data: telem.chart_data.map((p) => ({
            x: getFakeLocalDate(p.ts, tz),
            y: p.sig,
          })),
        },
      ],
      summary: telem.summary || {},
    };
  }, [analyticsData, device]);

  if (!datesInitialized || !device) return <div className="text-center p-5"><CSpinner /></div>;

  return (
    <div className="bg-light min-vh-100 p-2 p-md-4">
      <CContainer fluid className="dashboard-centered mobile-p-small">
        <CameraHeader deviceName={device.name} organizationName={device.organization_name} t={t} />

        <CCard className="mb-4 border-0 shadow-sm">
          <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom-0 p-3">
            <CameraFilterPanel
              t={t}
              isStaff={isStaff}
              isSuperuser={isSuperuser}
              filterMode="custom"
              setFilterMode={() => {}}
              datePreset={datePreset}
              handlePresetChange={handlePresetChange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              availableDates={availableDates}
              highlightedDates={loadingDates}
              currentLang="ru"
              fetchAnalytics={() => fetchRoadMarkingAnalytics()}
              loadingAnalytics={loadingAnalytics}
              device={device}
              hideVolumeMode={true}
              hideShiftMode={true}
              setRecalcStartDate={setRecalcStartDate}
              setRecalcModalVisible={setRecalcModalVisible}
              setFormatConfirmation={setFormatConfirmation}
              setFormatModalVisible={setFormatModalVisible}
              openFormatModal={openFormatModal}
              toast={toast}
            />
          </CCardHeader>

          <CCardBody>
            <RoadMarkingDashboard
              data={analyticsData}
              trajectoryData={trajectoryData}
              loadMapPoints={loadMapPoints}
              mapViewRef={mapViewRef}
              timezoneOffset={device?.timezone_offset || 0}
              onVideoJump={(ms) => handleTimeDoubleClick({ exactTime: ms })}
              timelineComponent={
                <GarbageTimelineSection
                  dateRangeForVis={{ start: startDate, end: endDate }}
                  statusTimelineItems={statusTimelineItems}
                  sitesTimelineItems={sessionsTimelineItems}
                  handleTimeDoubleClick={handleTimeDoubleClick}
                  currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                />
              }
            />
          </CCardBody>
        </CCard>

        {/* ПЛАВАЮЩИЕ КНОПКИ СПРАВА СНИЗУ */}
        <div className="floating-controls">
          <button
            className="floating-btn floating-btn-video"
            title="Видеоархив и Карта"
            onClick={() => setVideoModalVisible(true)}
          >
            <CIcon icon={cilMediaPlay} size="xl" />
          </button>
          <button
            className="floating-btn floating-btn-settings"
            title="Настройки, Телеметрия, Уведомления"
            onClick={() => setOffcanvasVisible(true)}
          >
            <CIcon icon={cilSettings} size="xl" />
          </button>
        </div>

        {/* ВЫЕЗЖАЮЩАЯ ПАНЕЛЬ НАСТРОЕК С ТЕЛЕМЕТРИЕЙ */}
        <COffcanvas
          placement="end"
          visible={offcanvasVisible}
          onHide={() => setOffcanvasVisible(false)}
          style={{ width: "30vw", minWidth: "400px", maxWidth: "100%", padding: 0, border: "none" }}
        >
          <COffcanvasBody className="p-0">
            <CameraTabs
              activeTab={activeSettingsTab}
              setActiveTab={setActiveSettingsTab}
              schedule={schedule}
              handleScheduleChange={(e) => setSchedule({ ...schedule, [e.target.name]: e.target.value })}
              handleWorkDayChange={(i) => setSchedule({ ...schedule, work_days: schedule.work_days ^ (1 << i) })}
              savingSchedule={savingSchedule}
              handleSaveSchedule={handleSaveSchedule}
              shifts={shifts}
              newShift={newShift}
              setNewShift={setNewShift}
              handleAddShift={() => {}}
              handleDeleteShift={() => {}}
              breaks={breaks}
              newBreak={newBreak}
              setNewBreak={setNewBreak}
              handleAddBreak={() => {}}
              handleDeleteBreak={() => {}}
              exceptions={exceptions}
              newException={newException}
              setNewException={setNewException}
              handleAddException={() => {}}
              handleDeleteException={() => {}}
              materials={materials}
              newMaterial={newMaterial}
              setNewMaterial={setNewMaterial}
              handleAddMaterial={() => {}}
              handleDeleteMaterial={() => {}}
              trajectoryData={trajectoryData}
              currentAbsoluteTime={currentAbsoluteTime}
              telemetryData={telemetryDataForTabs}
              analyticsData={analyticsData}
              cameraId={cameraId}
              isStaff={isStaff}
              isSuperuser={isSuperuser}
              onClose={() => setOffcanvasVisible(false)}
              onVideoJump={(timeMs) => handleTimeDoubleClick({ exactTime: timeMs })}
            />
          </COffcanvasBody>
        </COffcanvas>

        {/* МОДАЛЬНОЕ ОКНО ВИДЕОАРХИВА */}
        <CModal
          size="xl"
          alignment="center"
          className="video-archive-modal"
          visible={videoModalVisible}
          onClose={() => setVideoModalVisible(false)}
          fullscreen="xl"
        >
          <CModalHeader className="py-2 px-3">
            <CModalTitle className="fs-6 fw-bold">Видеоархив, Карта скоростей и Таймлайн (Дорожная разметка)</CModalTitle>
          </CModalHeader>
          <CModalBody className="bg-light p-2 d-flex flex-column">
            <div className="d-flex flex-column modal-body-inner">
              <div className="row g-2 modal-top-row align-items-stretch" style={{ minHeight: 0 }}>
                <div className="col-12 col-xl-8 d-flex flex-column modal-video-col">
                  <CameraVideoPlayer
                    t={t}
                    selectedVideo={selectedVideo}
                    showDetections={showDetections}
                    setShowDetections={setShowDetections}
                    setExportModalVisible={() => {}}
                    customPlayerRef={customPlayerRef}
                    seekToTime={seekToTime}
                    seekTriggerId={seekTriggerId}
                    canvasRef={canvasRef}
                  />
                </div>
                <div className="col-12 col-xl-4 d-flex flex-column modal-side-col">
                  <div className="d-flex mb-2 bg-white rounded border p-1 shadow-sm flex-shrink-0">
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === "map" ? "btn-primary" : "btn-light"}`}
                      onClick={() => setArchiveRightPanelMode("map")}
                    >
                      <CIcon icon={cilMap} className="me-2" /> Карта и Скорость
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === "archive" ? "btn-primary" : "btn-light"}`}
                      onClick={() => setArchiveRightPanelMode("archive")}
                    >
                      <CIcon icon={cilFolderOpen} className="me-2" /> Файлы архива
                    </button>
                  </div>
                  <div className="flex-grow-1 d-flex flex-column overflow-hidden position-relative modal-side-content">
                    {archiveRightPanelMode === "map" ? (
                      <CCard className="h-100 border-0 shadow-sm w-100 p-2 overflow-hidden">
                        <TruckMap
                          fill
                          track={trajectoryData}
                          loadPoints={loadMapPoints}
                          unloadPoints={[]}
                          viewState={mapViewRef}
                          playheadStore={playheadStore}
                          onPointClick={({ exactTime }) => handleTimeDoubleClick({ exactTime })}
                          activities={statusTimelineItems}
                        />
                      </CCard>
                    ) : (
                      <CameraArchiveList
                        t={t}
                        isStaff={false}
                        folders={folders}
                        selectedVideo={selectedVideo}
                        handleVideoSelect={handleVideoSelect}
                        timezoneOffset={device?.timezone_offset || 0}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* ТАЙМЛАЙН ПОД ВИДЕО */}
              {analyticsData && (
                <div className="mt-2 flex-shrink-0 modal-timeline-wrapper">
                  <GarbageTimelineSection
                    dateRangeForVis={{ start: startDate, end: endDate }}
                    statusTimelineItems={statusTimelineItems}
                    sitesTimelineItems={sessionsTimelineItems}
                    handleTimeDoubleClick={handleTimeDoubleClick}
                    currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                  />
                </div>
              )}
            </div>
          </CModalBody>
        </CModal>

        {/* МОДАЛЬНОЕ ОКНО ПЕРЕСЧЕТА */}
        <CModal visible={recalcModalVisible} onClose={() => setRecalcModalVisible(false)}>
          <CModalHeader onClose={() => setRecalcModalVisible(false)}>
            <CModalTitle>Пересчитать аналитику Дорожной разметки</CModalTitle>
          </CModalHeader>
          <CModalBody>
            <p>Выберите дату и время начала пересчета:</p>
            <DatePicker
              selected={recalcStartDate}
              onChange={setRecalcStartDate}
              className="form-control"
              showTimeSelect
              dateFormat="dd.MM.yyyy HH:mm"
              locale="ru"
            />
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setRecalcModalVisible(false)}>Отмена</CButton>
            <CButton color="primary" onClick={handleRecalculateSubmit} disabled={recalculating}>
              {recalculating ? <CSpinner size="sm" /> : "Начать пересчет"}
            </CButton>
          </CModalFooter>
        </CModal>
      </CContainer>

      {/* МОДАЛЬНЫЕ ОКНА ФОРМАТИРОВАНИЯ И ВОССТАНОВЛЕНИЯ */}
      <CameraModals
        device={device}
        isStaff={isStaff}
        recalcModalVisible={recalcModalVisible}
        setRecalcModalVisible={setRecalcModalVisible}
        recalcStartDate={recalcStartDate}
        setRecalcStartDate={setRecalcStartDate}
        handleRecalculateSubmit={handleRecalculateSubmit}
        recalculating={recalculating}
        formatModalVisible={formatModalVisible}
        setFormatModalVisible={setFormatModalVisible}
        formatMode={formatMode}
        setFormatMode={setFormatMode}
        formatDate={formatDate}
        setFormatDate={setFormatDate}
        formatConfirmation={formatConfirmation}
        setFormatConfirmation={setFormatConfirmation}
        formatActionTab={formatActionTab}
        setFormatActionTab={setFormatActionTab}
        formatStats={formatStats}
        handleClearData={handleClearData}
        handleRestoreData={handleRestoreData}
        formatting={formatting}
      />
    </div>
  );
};

export default RoadMarkingDetailPage;